import { useEffect } from 'react';
import { io, Socket } from 'socket.io-client';

import { API_BASE_URL } from '../api';

let socket: Socket | null = null;
const url = API_BASE_URL;
const options = {
  transports: ['websocket'],
};
const MAX_FILE_SIZE = 15 * 1024 * 1024;

const useSocket = (userId: string) => {
  useEffect(() => {
    if (!socket) {
      // Initialize socket connection only once
      socket = io(url, {
        ...options,
        auth: { userId },
      });
    }

    // Cleanup on unmount
    return () => {
      // Do not disconnect the socket here to maintain a single instance
    };
  }, [userId]);

  const emit = (event: string, ...args: unknown[]) => {
    socket?.emit(event, ...args);
  };

  const on = (event: string, callback: (...args: any[]) => void) => {
    socket?.on(event, callback);
  };

  const off = (event: string, callback?: (...args: unknown[]) => void) => {
    socket?.off(event, callback);
  };

  // Wire contract per MVPB-54: `uploadFile` takes a `files` array (mflow knowledge base "Chat
  // file attachments" doc) even though this hook's own public API is still single-file — the FE
  // UI has no multi-select yet, so this always sends a one-element array and reads back
  // `docs[0]`. `fileProcessed` is scoped to the uploading client only now (a prior BE bug
  // broadcast it to everyone), so a `.once()` listener per call is safe and self-cleaning —
  // no more listener accumulating on every upload (the old `on(...)` here was never removed).
  const uploadFile = (
    file: File,
    userId: string,
    chatId: string | null,
    onSuccess: (docId: string) => void,
    onError: (error: string) => void,
  ) => {
    if (file.size > MAX_FILE_SIZE) {
      onError('File size exceeds the maximum limit of 15MB.');
      return;
    }

    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = () => {
      socket?.once(
        'fileProcessed',
        (data: { userId: string; chatId: string; docs: { docId?: string; fileName?: string; error?: string }[] }) => {
          const result = data.docs?.[0];
          if (!result || result.error) {
            onError(result?.error ?? 'Upload failed.');
          } else if (result.docId) {
            onSuccess(result.docId);
          } else {
            onError('Upload failed.');
          }
        },
      );

      emit('uploadFile', {
        files: [
          {
            file: reader.result,
            fileType: file.name.split('.').pop()?.toLowerCase(),
            fileName: file.name,
          },
        ],
        userId,
        chatId,
      });
    };
  };

  return { socket, emit, on, off, uploadFile };
};

export default useSocket;
